/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-nested-ternary */
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Combobox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { useController, get } from 'react-hook-form';
import ToolTip from '../../../elements/ToolTip';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const MAX_OPTIONS_TO_RENDER = 50;

export default function LocationCombobox({ id, label, toolTip, placeholder, optional, control, errors, validationRules, options, disabled, formatting }) {
  const { field, fieldState } = useController({ name: id, control, rules: validationRules });
  const { t: tSchema } = useTranslation('app');
  const { t: tAddAccount } = useTranslation('addAccount');
  const errorMessage = fieldState?.error?.message || get(errors, id, {})?.message || '';

  const calculatedLabel = label || tSchema(`schema.realEstate.account.valuationParameters.${id.split('.').pop()}.label`);
  const calculatedToolTip = toolTip || tSchema(`schema.realEstate.account.valuationParameters.${id.split('.').pop()}.tooltip`);
  const calculatedPlaceholder = placeholder || tSchema(`schema.realEstate.account.valuationParameters.${id.split('.').pop()}.placeholder`);

  const [query, setQuery] = useState('');
  const [internalValue, setInternalValue] = useState(field.value || '');
  const [pendingValue, setPendingValue] = useState(field.value || '');

  useEffect(() => {
    setInternalValue(field.value || '');
    setPendingValue(field.value || '');
  }, [field.value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (pendingValue !== field.value) {
        field.onChange(pendingValue);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [field, pendingValue]);

  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((option) => ({ raw: option, lc: option.toLocaleLowerCase('de-DE') }));
  }, [options]);

  const filteredOptions = useMemo(() => {
    if (normalizedOptions.length === 0) return [];
    const trimmedQuery = query.trim().toLocaleLowerCase('de-DE');
    const source = trimmedQuery ? normalizedOptions.filter((item) => item.lc.includes(trimmedQuery)) : normalizedOptions;
    return source.slice(0, MAX_OPTIONS_TO_RENDER).map((item) => item.raw);
  }, [normalizedOptions, query]);

  const queryLimitedResults = query.trim().length > 0 && filteredOptions.length === MAX_OPTIONS_TO_RENDER;

  function handleInputChange(event) {
    const { value } = event.target;
    setQuery(value);
    if (value === '') {
      setInternalValue('');
      setPendingValue('');
    }
  }

  function handleClear() {
    setQuery('');
    setInternalValue('');
    setPendingValue('');
  }

  const selectedValue = internalValue;

  return (
    <div className={`relative ${formatting}`}>
      <div className="flex justify-between">
        <label htmlFor={id} className="text-sm font-medium text-gray-700 flex items-center gap-1">
          {calculatedLabel}
          <ToolTip info={calculatedToolTip} />
        </label>
        <span className="text-sm text-gray-500">{optional ? 'Optional' : ''}</span>
      </div>
      <Combobox
        value={selectedValue}
        onChange={(value) => {
          setInternalValue(value || '');
          setPendingValue(value || '');
          setQuery('');
        }}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <div className="relative mt-1">
              <Combobox.Input
                id={id}
                className={classNames(
                  'w-full rounded-md border bg-white py-2 pl-3 pr-10 shadow-sm focus:outline-none focus:ring-1 sm:text-sm',
                  errorMessage
                    ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500'
                    : disabled
                      ? 'border-gray-200 text-gray-400 placeholder-gray-300 bg-gray-100 cursor-not-allowed'
                      : 'border-gray-300 placeholder-gray-400 focus:border-brandBlue-500 focus:ring-brandBlue-600',
                )}
                displayValue={(value) => value || ''}
                onChange={handleInputChange}
                onBlur={field.onBlur}
                placeholder={disabled ? '' : calculatedPlaceholder}
                disabled={disabled}
                autoComplete="off"
              />
              {!disabled && selectedValue && (
                <button type="button" onClick={handleClear} className="absolute inset-y-0 right-7 flex items-center pr-1 text-gray-400 hover:text-gray-600" aria-label={`Clear ${calculatedLabel}`}>
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
              <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </Combobox.Button>
              <div className={`${!errorMessage ? 'hidden' : ''} pointer-events-none absolute inset-y-0 right-0 pr-8 flex items-center`}>
                <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
              </div>
            </div>
            <Transition
              show={open && !disabled && (filteredOptions.length > 0 || query.trim() !== '')}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery('')}
            >
              <Combobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                {filteredOptions.length === 0 ? (
                  <div className="cursor-default select-none py-2 px-3 text-gray-500">{tAddAccount('R1.noMatches', 'No matches found')}</div>
                ) : (
                  filteredOptions.map((option) => (
                    <Combobox.Option
                      key={option}
                      className={({ active }) => classNames('relative cursor-default select-none py-2 pl-3 pr-9', active ? 'bg-brandBlue-500 text-white' : 'text-gray-900')}
                      value={option}
                    >
                      {({ selected }) => <span className={classNames('block truncate', selected ? 'font-semibold' : 'font-normal')}>{option}</span>}
                    </Combobox.Option>
                  ))
                )}
                {queryLimitedResults && (
                  <div className="cursor-default select-none py-2 px-3 text-gray-400 text-xs">{tAddAccount('R1.locationResultsLimited', 'Showing first matches. Keep typing to narrow down.')}</div>
                )}
              </Combobox.Options>
            </Transition>
          </>
        )}
      </Combobox>
      <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
    </div>
  );
}
LocationCombobox.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  toolTip: PropTypes.string,
  placeholder: PropTypes.string,
  optional: PropTypes.bool,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  validationRules: PropTypes.object,
  options: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  formatting: PropTypes.string,
};
LocationCombobox.defaultProps = {
  label: null,
  toolTip: null,
  placeholder: null,
  optional: true,
  validationRules: undefined,
  options: [],
  disabled: false,
  formatting: '',
};
